<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DSelectFieldSearch,
  DModal,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  getPayroll,
  getConcepts,
  getReportRequest,
  convertArrayToString,
  getProcessedPayrolls,
  filterProcessSelectReports,
  ParametersRentTemplate,
  ReportRentTemplate,
  generateRentTemplate,
  getEmployeesReport
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const itemsPayrolls = ref<any[]>([])
const allConcepts = ref<any[]>([])
const itemsConcepts = ref<Map<string, any>>(new Map())

const itemsEmployees = ref<string[]>([])
const searchSalaryAmountConcepts = ref<string>('')
const searchSupplementaryPensionsConcepts = ref<string>('')
const searchWithheldTaxSLConcepts = ref<string>('')
const searchRelativeCreditSLConcepts = ref<string>('')
const searchSalaryAmountConceptsOthersEmployers = ref<string>('')
const searchRetentionBaseDGPConcepts = ref<string>('')
const searchWithheldTaxDGPConcepts = ref<string>('')
const searchRetentionBaseOISEConcepts = ref<string>('')
const searchWithheldTaxOISEConcepts = ref<string>('')
const searchRetentionBaseJPConcepts = ref<string>('')
const searchWithheldTaxJPConcepts = ref<string>('')

const urlFile = ref(null)
const refresh = ref()

/* Esquema de validación campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') })),
  salaryAmountConcepts: string().required(t('form.valid.required')),
  supplementaryPensionsConcepts: string(),
  withheldTaxSLConcepts: array()
    .min(1, t('form.valid.required'))
    .required(t('form.valid.required', { field: t('reports.fields.withheldTaxSLConcepts') })),
  relativeCreditSLConcepts: string().required(t('form.valid.required')),
  salaryAmountConceptsOthersEmployers: string(),
  retentionBaseDGPConcepts: string(),
  withheldTaxDGPConcepts: string(),
  retentionBaseOISEConcepts: string(),
  withheldTaxOISEConcepts: string(),
  retentionBaseJPConcepts: string(),
  withheldTaxJPConcepts: string()
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<ParametersRentTemplate>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: employees } = useField<any[]>(ref('employees'))
const { value: salaryAmountConcepts } = useField<string>('salaryAmountConcepts')
const { value: salaryAmountConceptsOthersEmployers } = useField<string>(
  'salaryAmountConceptsOthersEmployers'
)
const { value: retentionBaseDGPConcepts } = useField<string>(
  'retentionBaseDGPConcepts'
)
const { value: withheldTaxDGPConcepts } = useField<string>(
  'withheldTaxDGPConcepts'
)
const { value: retentionBaseOISEConcepts } = useField<string>(
  'retentionBaseOISEConcepts'
)
const { value: withheldTaxOISEConcepts } = useField<string>(
  'withheldTaxOISEConcepts'
)
const { value: retentionBaseJPConcepts } = useField<string>(
  'retentionBaseJPConcepts'
)
const { value: withheldTaxJPConcepts } = useField<string>(
  'withheldTaxJPConcepts'
)
const { value: supplementaryPensionsConcepts } = useField<string>(
  'supplementaryPensionsConcepts'
)
const { value: withheldTaxSLConcepts } = useField<string[]>(ref('withheldTaxSLConcepts'))
const { value: relativeCreditSLConcepts } = useField<string>(
  'relativeCreditSLConcepts'
)

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) => {
    // Si items es un Map, convertir sus valores a array
    let itemsArray = []
    if (items instanceof Map) {
      itemsArray = Array.from(items.values())
    } else {
      itemsArray = items
    }

    return itemsArray.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search?.toString().toLowerCase()) > -1
    )
  }
})

/* Filtrar codigos de conceptos */
const filterCodeConcepts = async (conceptId: string) => {
  let conceptCode = allConcepts.value.find(item => item.id === conceptId)?.code
  return allConcepts.value
    .filter(item => item.code === conceptCode)
    .map(concept => concept.id)
}

const filterCodeConceptsArray = async (conceptIds: string[]) => {
  const filteredConcepts = await Promise.all(
    conceptIds.map(async (conceptId: string) => {
      return filterCodeConcepts(conceptId)
    })
  )
  return filteredConcepts.flat()
}

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit(async (values: any) => {
  await Promise.all([
    filterCodeConcepts(values.salaryAmountConcepts),
    filterCodeConcepts(values.supplementaryPensionsConcepts),
    filterCodeConceptsArray(values.withheldTaxSLConcepts),
    filterCodeConcepts(values.relativeCreditSLConcepts),
    filterCodeConcepts(values.salaryAmountConceptsOthersEmployers),
    filterCodeConcepts(values.retentionBaseDGPConcepts),
    filterCodeConcepts(values.withheldTaxDGPConcepts),
    filterCodeConcepts(values.retentionBaseOISEConcepts),
    filterCodeConcepts(values.withheldTaxOISEConcepts),
    filterCodeConcepts(values.retentionBaseJPConcepts),
    filterCodeConcepts(values.withheldTaxJPConcepts)
  ]).then(
    ([
      salaryAmountConcepts,
      supplementaryPensionsConcepts,
      withheldTaxSLConcepts,
      relativeCreditSLConcepts,
      salaryAmountConceptsOthersEmployers,
      retentionBaseDGPConcepts,
      withheldTaxDGPConcepts,
      retentionBaseOISEConcepts,
      withheldTaxOISEConcepts,
      retentionBaseJPConcepts,
      withheldTaxJPConcepts
    ]) => {
      values.salaryAmountConcepts = salaryAmountConcepts
      values.supplementaryPensionsConcepts = supplementaryPensionsConcepts
      values.withheldTaxSLConcepts = withheldTaxSLConcepts
      values.relativeCreditSLConcepts = relativeCreditSLConcepts
      values.salaryAmountConceptsOthersEmployers = salaryAmountConceptsOthersEmployers
      values.retentionBaseDGPConcepts = retentionBaseDGPConcepts
      values.withheldTaxDGPConcepts = withheldTaxDGPConcepts
      values.retentionBaseOISEConcepts = retentionBaseOISEConcepts
      values.withheldTaxOISEConcepts = withheldTaxOISEConcepts
      values.retentionBaseJPConcepts = retentionBaseJPConcepts
      values.withheldTaxJPConcepts = withheldTaxJPConcepts
    }
  )

  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportRentTemplate = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateRentTemplate(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar empleados y conceptos */
const listConcepts = async () => {
  if (!payrolls.value?.length) return
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = new Map()
  try {
    const strPayrolls = await convertArrayToString(payrolls.value)
    allConcepts.value = await getConcepts(strPayrolls, 'rentTemplate', '', '')

    // Crear Map donde la clave es el código y el valor es el concepto
    const conceptsByCode = new Map()

    allConcepts.value.forEach((item: any) => {
      conceptsByCode.set(item.code, item)
    })

    // itemsConcepts será un Map, no un array
    itemsConcepts.value = conceptsByCode

    if (itemsConcepts.value.size === 0) {
      notifications.addNotification({
        title: t('reports.notification.noConceptsFound'),
        text: t('reports.notification.noConcepts'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar nóminas procesadas */
const getListProcessedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  let _dateIni = startDate.value
  let _dateEnd = endDate.value
  let _employees: any = []
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      _dateIni != undefined &&
      _dateEnd != undefined &&
      payrolls.value?.length > 0
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strProcesses = ''
      let strPayrolls = await convertArrayToString(payrolls.value)
      let _ProcessedPayroll = await getProcessedPayrolls(
        strPayrolls,
        strProcesses,
        _dateIni,
        _dateEnd,
        'CLOSED'
      )
      loading.value = false
      _employees = validateDataProcessedPayroll(_ProcessedPayroll)
      const strEmployees = await convertArrayToString(_employees)
      getEmployees(strEmployees)
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Validar nomina procesada */
const validateDataProcessedPayroll = (data: any) => {
  const dataFilters = {
    processedPayroll: data,
    payrolls: payrolls.value,
    process: [
      'N','A','E','L','R','U','M'
    ],
    status: ['CLOSED']
  }
  return filterProcessSelectReports(dataFilters)
}

const getEmployees = async (employeesIds: string) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      itemsEmployees.value = await getEmployeesReport('', '', employeesIds)
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

const onPayrollsChange = () => {
  employees.value = []
  itemsEmployees.value = []
  withheldTaxSLConcepts.value = []
  salaryAmountConcepts.value = ''
  supplementaryPensionsConcepts.value = ''
  relativeCreditSLConcepts.value = ''
  salaryAmountConceptsOthersEmployers.value = ''
  retentionBaseDGPConcepts.value = ''
  withheldTaxDGPConcepts.value = ''
  retentionBaseOISEConcepts.value = ''
  withheldTaxOISEConcepts.value = ''
  retentionBaseJPConcepts.value = ''
  withheldTaxJPConcepts.value = ''

  getListProcessedPayroll()
  listConcepts()
}

/* Validar fecha */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsPayrolls.value = await getPayroll(store.user?.id as string)
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)

    await listConcepts()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.rentTemplate')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="onPayrollsChange()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="validateDate, getListProcessedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="validateDate, getListProcessedPayroll()" />
                </div>
                <hr class="col-span-6" />

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.salaryAmountConcepts')"
                    v-model="salaryAmountConcepts"
                    :required="true"
                    :options="
                      filterOptions(itemsConcepts, searchSalaryAmountConcepts)
                    "
                    @filter="searchSalaryAmountConcepts = $event"
                    :error="errors.salaryAmountConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.supplementaryPensionsConcepts')"
                    v-model="supplementaryPensionsConcepts"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchSupplementaryPensionsConcepts
                      )
                    "
                    @filter="searchSupplementaryPensionsConcepts = $event"
                    :error="errors.supplementaryPensionsConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.withheldTaxSLConcepts')"
                    v-model="withheldTaxSLConcepts"
                    :headerTitle="t('reports.fields.withheldTaxSLConcepts')"
                    :required="true"
                    :options= "filterOptions(itemsConcepts, searchWithheldTaxSLConcepts)"
                    :error="errors.withheldTaxSLConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.relativeCreditSLConcepts')"
                    v-model="relativeCreditSLConcepts"
                    :required="true"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchRelativeCreditSLConcepts
                      )
                    "
                    @filter="searchRelativeCreditSLConcepts = $event"
                    :error="errors.relativeCreditSLConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.salaryAmountConceptsOthersEmployers')"
                    v-model="salaryAmountConceptsOthersEmployers"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchSalaryAmountConceptsOthersEmployers
                      )
                    "
                    @filter="searchSalaryAmountConceptsOthersEmployers = $event"
                    :error="errors.salaryAmountConceptsOthersEmployers"
                    :disabled="!payrolls?.length" />
                </div>
                
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.retentionBaseDGPConcepts')"
                    v-model="retentionBaseDGPConcepts"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchRetentionBaseDGPConcepts
                      )
                    "
                    @filter="searchRetentionBaseDGPConcepts = $event"
                    :error="errors.retentionBaseDGPConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.withheldTaxDGPConcepts')"
                    v-model="withheldTaxDGPConcepts"
                    :options="
                      filterOptions(itemsConcepts, searchWithheldTaxDGPConcepts)
                    "
                    @filter="searchWithheldTaxDGPConcepts = $event"
                    :error="errors.withheldTaxDGPConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.retentionBaseOISEConcepts')"
                    v-model="retentionBaseOISEConcepts"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchRetentionBaseOISEConcepts
                      )
                    "
                    @filter="searchRetentionBaseOISEConcepts = $event"
                    :error="errors.retentionBaseOISEConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.withheldTaxOISEConcepts')"
                    v-model="withheldTaxOISEConcepts"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchWithheldTaxOISEConcepts
                      )
                    "
                    @filter="searchWithheldTaxOISEConcepts = $event"
                    :error="errors.withheldTaxOISEConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.retentionBaseJPConcepts')"
                    v-model="retentionBaseJPConcepts"
                    :options="
                      filterOptions(
                        itemsConcepts,
                        searchRetentionBaseJPConcepts
                      )
                    "
                    @filter="searchRetentionBaseJPConcepts = $event"
                    :error="errors.retentionBaseJPConcepts"
                    :disabled="!payrolls?.length" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.withheldTaxJPConcepts')"
                    v-model="withheldTaxJPConcepts"
                    :options="
                      filterOptions(itemsConcepts, searchWithheldTaxJPConcepts)
                    "
                    @filter="searchWithheldTaxJPConcepts = $event"
                    :error="errors.withheldTaxJPConcepts"
                    :disabled="!payrolls?.length" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
